import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import RelatedLinks from '../../components/feature/RelatedLinks';

interface Toast {
  type: 'success' | 'error';
  message: string;
}

interface Post {
  id: string;
  category: string;
  title: string;
  content: string;
  empathy_count: number;
  created_at: string;
  reply_count?: number;
}

export default function MyPage() {
  const { user, profile, loading: authLoading, signOut, updateProfile } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [nickname, setNickname] = useState('');
  const [area, setArea] = useState('');
  const [childAge, setChildAge] = useState('');
  const [careType, setCareType] = useState('');
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [myPosts, setMyPosts] = useState<Post[]>([]);
  const [loadingPosts, setLoadingPosts] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (profile) {
      setNickname(profile.nickname || '');
      setArea(profile.area || '');
      setChildAge(profile.child_age || '');
      setCareType(profile.care_type || '');
    }
  }, [profile]);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3500);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  useEffect(() => {
    if (user) {
      fetchMyPosts();
    }
  }, [user]);

  const fetchMyPosts = async () => {
    if (!user) return;
    
    setLoadingPosts(true);
    try {
      const { data: postsData, error } = await supabase
        .from('posts')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // 各投稿の返信数を取得
      const postsWithReplies = await Promise.all(
        (postsData || []).map(async (post) => {
          const { count } = await supabase
            .from('replies')
            .select('*', { count: 'exact', head: true })
            .eq('post_id', post.id);
          
          return { ...post, reply_count: count || 0 };
        })
      );

      setMyPosts(postsWithReplies);
    } catch (error) {
      console.error('投稿履歴の取得エラー:', error);
    } finally {
      setLoadingPosts(false);
    }
  };

  const handleAvatarSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      setToast({ type: 'error', message: '画像サイズは2MB以下にしてください' });
      return;
    }

    if (!file.type.startsWith('image/')) {
      setToast({ type: 'error', message: '画像ファイルを選択してください' });
      return;
    }

    setAvatarFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const uploadAvatar = async (): Promise<string | null> => {
    if (!avatarFile || !user) return profile?.avatar_url || null;

    setUploading(true);
    try {
      const fileExt = avatarFile.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}.${fileExt}`;

      if (profile?.avatar_url) {
        const oldPath = profile.avatar_url.split('/avatars/')[1];
        if (oldPath) {
          await supabase.storage.from('avatars').remove([oldPath]);
        }
      }

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, avatarFile, {
          cacheControl: '3600',
          upsert: true,
        });

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      return data.publicUrl;
    } catch (error) {
      console.error('アバターアップロードエラー:', error);
      setToast({ type: 'error', message: '画像のアップロードに失敗しました' });
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;
    if (!nickname.trim()) {
      setToast({ type: 'error', message: 'ニックネームを入力してください' });
      return;
    }

    setSaving(true);
    try {
      const newAvatarUrl = await uploadAvatar();

      await updateProfile({
        nickname: nickname.trim(),
        area: area.trim(),
        child_age: childAge.trim(),
        care_type: careType.trim(),
        ...(newAvatarUrl !== undefined && newAvatarUrl !== null && { avatar_url: newAvatarUrl }),
      });

      setAvatarFile(null);
      setAvatarPreview(null);
      setIsEditing(false);
      setToast({ type: 'success', message: 'プロフィールを更新しました' });
    } catch (error) {
      console.error('プロフィール更新エラー:', error);
      setToast({ type: 'error', message: '更新に失敗しました。もう一度お試しください' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (profile) {
      setNickname(profile.nickname || '');
      setArea(profile.area || '');
      setChildAge(profile.child_age || '');
      setCareType(profile.care_type || '');
    }
    setAvatarFile(null);
    setAvatarPreview(null);
    setIsEditing(false);
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('ログアウトエラー:', error);
      setToast({ type: 'error', message: 'ログアウトに失敗しました' });
    }
  };

  const displayAvatar = avatarPreview || profile?.avatar_url;

  const getCategoryStyle = (category: string) => {
    switch (category) {
      case 'つぶやき・モヤモヤ':
        return 'bg-rose-100 text-rose-700';
      case '教えて！ケアの知恵':
        return 'bg-amber-100 text-amber-700';
      case '保育園・社会参加':
        return 'bg-emerald-100 text-emerald-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return '今日';
    } else if (diffDays === 1) {
      return '昨日';
    } else if (diffDays < 7) {
      return `${diffDays}日前`;
    } else {
      return date.toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' });
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-rose-50 flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-rose-50">
      {/* トースト通知 */}
      {toast && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[100] animate-fade-in">
          <div className={`flex items-center space-x-3 px-5 py-3 rounded-xl shadow-xl ${toast.type === 'success' ? 'bg-teal-600 text-white' : 'bg-rose-600 text-white'}`}>
            <i className={`text-xl ${toast.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'}`}></i>
            <span className="text-sm font-medium whitespace-nowrap">{toast.message}</span>
            <button onClick={() => setToast(null)} className="ml-2 w-6 h-6 flex items-center justify-center hover:bg-white/20 rounded-full cursor-pointer">
              <i className="ri-close-line text-sm"></i>
            </button>
          </div>
        </div>
      )}

      {/* ナビゲーション */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-teal-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center">
                <i className="ri-ship-line text-white text-xl"></i>
              </div>
              <span className="text-lg sm:text-xl font-bold bg-gradient-to-r from-teal-600 to-teal-800 bg-clip-text text-transparent">
                みやぎスマイル・ポート
              </span>
            </Link>
            <div className="flex items-center space-x-3 sm:space-x-4">
              <Link to="/board" className="text-gray-600 hover:text-teal-600 transition-colors text-sm whitespace-nowrap cursor-pointer">
                休憩室
              </Link>
              <Link to="/manual" className="text-gray-600 hover:text-teal-600 transition-colors text-sm whitespace-nowrap cursor-pointer">
                マニュアル
              </Link>
              <div className="flex items-center space-x-2 px-3 py-1.5 bg-teal-50 rounded-full">
                <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center">
                  <i className="ri-user-smile-line text-white text-sm"></i>
                </div>
                <span className="text-sm font-medium text-teal-700 hidden sm:inline">{profile?.nickname || 'ユーザー'}</span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* ページタイトル */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-1">マイページ</h1>
          <p className="text-sm text-gray-500">プロフィール情報の確認・変更ができます</p>
        </div>

        {/* プロフィールカード */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-6">
          {/* ヘッダー背景 */}
          <div className="h-28 sm:h-36 bg-gradient-to-r from-teal-400 via-teal-500 to-emerald-500 relative">
            <div className="absolute inset-0 opacity-20">
              <div className="absolute top-4 left-8 w-16 h-16 rounded-full border-2 border-white/30"></div>
              <div className="absolute bottom-2 right-12 w-10 h-10 rounded-full border-2 border-white/20"></div>
              <div className="absolute top-6 right-32 w-6 h-6 rounded-full bg-white/20"></div>
            </div>
          </div>

          {/* アバターエリア */}
          <div className="relative px-5 sm:px-8 pb-6">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between -mt-14 sm:-mt-16">
              <div className="relative group">
                <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-full overflow-hidden bg-white border-4 border-white shadow-lg">
                  {displayAvatar ? (
                    <img src={displayAvatar} alt="プロフィール画像" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-teal-100 to-teal-200">
                      <img 
                        src="https://static.readdy.ai/image/8d13d909127b965a1b96285c4b9af1e2/abea7e377127d504f93f3b47dac5173e.png" 
                        alt="デフォルトアバター" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                </div>
                {isEditing && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute bottom-1 right-1 w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center text-white shadow-lg hover:bg-teal-700 transition-colors cursor-pointer"
                  >
                    <i className="ri-camera-line text-lg"></i>
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png"
                  onChange={handleAvatarSelect}
                  className="hidden"
                />
              </div>

              <div className="mt-4 sm:mt-0 sm:mb-2 flex items-center gap-3">
                {!isEditing ? (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-50 text-teal-700 rounded-xl font-medium hover:bg-teal-100 transition-colors cursor-pointer whitespace-nowrap text-sm"
                  >
                    <i className="ri-edit-line"></i>
                    編集する
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handleCancel}
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-100 text-gray-600 rounded-xl font-medium hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap text-sm"
                    >
                      キャンセル
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={saving || uploading}
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white rounded-xl font-medium hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saving || uploading ? (
                        <>
                          <i className="ri-loader-4-line animate-spin"></i>
                          {uploading ? '画像アップロード中...' : '保存中...'}
                        </>
                      ) : (
                        <>
                          <i className="ri-check-line"></i>
                          保存する
                        </>
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* 編集モード：画像変更ヒント */}
            {isEditing && (
              <p className="mt-3 text-xs text-gray-500 flex items-center gap-1">
                <i className="ri-information-line"></i>
                プロフィール画像をタップして変更できます（JPG/PNG、2MB以下）
              </p>
            )}
          </div>

          {/* プロフィール情報 */}
          <div className="px-5 sm:px-8 pb-6 sm:pb-8">
            <div className="space-y-5">
              {/* ニックネーム */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                  ニックネーム
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="ニックネームを入力"
                  />
                ) : (
                  <p className="text-base font-medium text-gray-800">{profile?.nickname || '未設定'}</p>
                )}
              </div>

              {/* ユーザータイプ */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                  ユーザータイプ
                </label>
                <div className="flex items-center gap-2">
                  {profile?.user_type === 'partner' ? (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                      <i className="ri-user-star-line"></i>
                      スマイル・パートナー
                      {profile?.partner_role && (
                        <span className="text-purple-500">（{profile.partner_role}）</span>
                      )}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm font-medium">
                      <i className="ri-user-heart-line"></i>
                      保護者
                    </span>
                  )}
                </div>
              </div>

              <div className="border-t border-gray-100 pt-5 grid grid-cols-1 sm:grid-cols-2 gap-5">
                {/* お住まいの地域 */}
                <div>
                  <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                    お住まいの地域
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={area}
                      onChange={(e) => setArea(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="例：宮城県仙台市"
                    />
                  ) : (
                    <p className="text-sm text-gray-700">{profile?.area || <span className="text-gray-400">未設定</span>}</p>
                  )}
                </div>

                {/* お子さんの年齢 */}
                <div>
                  <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                    お子さんの年齢
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={childAge}
                      onChange={(e) => setChildAge(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="例：2歳"
                    />
                  ) : (
                    <p className="text-sm text-gray-700">{profile?.child_age || <span className="text-gray-400">未設定</span>}</p>
                  )}
                </div>
              </div>

              {/* ケアの種類 */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                  ケアの種類
                </label>
                {isEditing ? (
                  <>
                    <textarea
                      value={careType}
                      onChange={(e) => setCareType(e.target.value.slice(0, 500))}
                      rows={3}
                      maxLength={500}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                      placeholder="例：経管栄養、酸素療法など"
                    />
                    <p className="mt-1 text-xs text-gray-400 text-right">{careType.length}/500</p>
                  </>
                ) : (
                  <p className="text-sm text-gray-700">{profile?.care_type || <span className="text-gray-400">未設定</span>}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* 投稿履歴セクション */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-6">
          <div className="px-5 sm:px-6 py-4 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <i className="ri-file-list-3-line text-teal-600 text-xl"></i>
                <h2 className="text-base font-bold text-gray-800">投稿履歴</h2>
                {!loadingPosts && (
                  <span className="px-2 py-0.5 bg-teal-100 text-teal-700 rounded-full text-xs font-medium">
                    {myPosts.length}件
                  </span>
                )}
              </div>
              <Link
                to="/board"
                className="text-sm text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1 cursor-pointer"
              >
                新規投稿
                <i className="ri-add-line"></i>
              </Link>
            </div>
          </div>

          <div className="divide-y divide-gray-100">
            {loadingPosts ? (
              <div className="p-8 text-center">
                <div className="animate-spin w-8 h-8 border-3 border-teal-500 border-t-transparent rounded-full mx-auto mb-3"></div>
                <p className="text-sm text-gray-500">読み込み中...</p>
              </div>
            ) : myPosts.length === 0 ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-chat-off-line text-2xl text-gray-400"></i>
                </div>
                <p className="text-gray-600 mb-2 text-sm">まだ投稿がありません</p>
                <p className="text-xs text-gray-400 mb-4">休憩室で気持ちを共有してみませんか？</p>
                <Link
                  to="/board"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-xl text-sm font-medium hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line"></i>
                  投稿する
                </Link>
              </div>
            ) : (
              myPosts.map((post) => (
                <Link
                  key={post.id}
                  to={`/board/post/${post.id}`}
                  className="block p-4 sm:p-5 hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getCategoryStyle(post.category)}`}>
                        {post.category}
                      </span>
                      <span className="text-xs text-gray-400">{formatDate(post.created_at)}</span>
                    </div>
                    <i className="ri-arrow-right-s-line text-gray-400 flex-shrink-0"></i>
                  </div>
                  <h3 className="text-sm font-bold text-gray-800 mb-1.5 line-clamp-1">{post.title}</h3>
                  <p className="text-xs text-gray-500 line-clamp-2 mb-3">{post.content}</p>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1 text-rose-500">
                      <i className="ri-heart-3-line text-sm"></i>
                      <span className="text-xs font-medium">{post.empathy_count}</span>
                    </div>
                    <div className="flex items-center gap-1 text-teal-500">
                      <i className="ri-chat-3-line text-sm"></i>
                      <span className="text-xs font-medium">{post.reply_count || 0}</span>
                    </div>
                  </div>
                </Link>
              ))
            )}
          </div>

          {myPosts.length > 0 && (
            <div className="px-5 sm:px-6 py-3 bg-gray-50 border-t border-gray-100">
              <Link
                to="/board"
                className="text-sm text-teal-600 hover:text-teal-700 font-medium flex items-center justify-center gap-1 cursor-pointer"
              >
                休憩室で全ての投稿を見る
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          )}
        </div>

        {/* クイックリンク */}
        <div className="bg-white rounded-2xl shadow-sm p-5 sm:p-6 mb-6">
          <h2 className="text-base font-bold text-gray-800 mb-4">クイックリンク</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Link to="/board" className="flex items-center gap-3 p-4 rounded-xl bg-rose-50 hover:bg-rose-100 transition-colors cursor-pointer group">
              <div className="w-10 h-10 flex items-center justify-center bg-rose-200 rounded-xl">
                <i className="ri-chat-3-line text-rose-600 text-lg"></i>
              </div>
              <div>
                <p className="text-sm font-bold text-gray-800 group-hover:text-rose-700 transition-colors">みんなの休憩室</p>
                <p className="text-xs text-gray-500">匿名のお悩み掲示板</p>
              </div>
              <i className="ri-arrow-right-s-line text-gray-400 ml-auto"></i>
            </Link>
            <Link to="/manual" className="flex items-center gap-3 p-4 rounded-xl bg-amber-50 hover:bg-amber-100 transition-colors cursor-pointer group">
              <div className="w-10 h-10 flex items-center justify-center bg-amber-200 rounded-xl">
                <i className="ri-book-open-line text-amber-600 text-lg"></i>
              </div>
              <div>
                <p className="text-sm font-bold text-gray-800 group-hover:text-amber-700 transition-colors">お守りマニュアル</p>
                <p className="text-xs text-gray-500">専門家の推奨マニュアル</p>
              </div>
              <i className="ri-arrow-right-s-line text-gray-400 ml-auto"></i>
            </Link>
          </div>
        </div>

        {/* ログアウト */}
        <div className="bg-white rounded-2xl shadow-sm p-5 sm:p-6">
          <button
            onClick={() => setShowLogoutConfirm(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 text-rose-600 bg-rose-50 rounded-xl hover:bg-rose-100 transition-colors cursor-pointer text-sm font-medium whitespace-nowrap"
          >
            <i className="ri-logout-box-r-line"></i>
            ログアウト
          </button>
        </div>
      </div>

      {/* ログアウト確認モーダル */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-logout-box-r-line text-rose-600 text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">ログアウトしますか？</h3>
              <p className="text-sm text-gray-500">再度ログインが必要になります</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                キャンセル
              </button>
              <button
                onClick={handleLogout}
                className="flex-1 px-4 py-2.5 bg-rose-600 text-white rounded-xl font-medium hover:bg-rose-700 transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                ログアウト
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 関連リンク */}
      <RelatedLinks />

      {/* フッター */}
      <footer className="bg-gradient-to-r from-teal-700 to-teal-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-6 sm:mb-8">
            <div>
              <div className="flex items-center space-x-3 mb-3 sm:mb-4">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <i className="ri-ship-line text-white text-xl"></i>
                </div>
                <span className="text-lg sm:text-xl font-bold">みやぎスマイル・ポート</span>
              </div>
              <p className="text-teal-100 text-xs sm:text-sm">医療的ケア児と家族の港</p>
            </div>
            <div>
              <h4 className="font-bold mb-2 sm:mb-3 text-sm sm:text-base">リンク</h4>
              <ul className="space-y-2 text-xs sm:text-sm text-teal-100">
                <li><Link to="/" className="hover:text-white transition-colors cursor-pointer">ホーム</Link></li>
                <li><Link to="/board" className="hover:text-white transition-colors cursor-pointer">みんなの休憩室</Link></li>
                <li><Link to="/manual" className="hover:text-white transition-colors cursor-pointer">お守りマニュアル</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-2 sm:mb-3 text-sm sm:text-base">お問い合わせ</h4>
              <p className="text-xs sm:text-sm text-teal-100">support@smile-port.jp</p>
            </div>
          </div>
          <div className="border-t border-teal-600 pt-6 sm:pt-8 text-center text-xs sm:text-sm text-teal-100">
            <p>&copy; 2025 みやぎスマイル・ポート. All rights reserved. | <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">Powered by Readdy</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
